/** @format */

import {
  AnalyticsPricingBuckets,
  BundleData,
  ISkuLocation,
  LoyaltyBalance,
  MissingImageEventInfo,
  ProductSku,
  ProductStyle,
  ProductsData,
  SKUDetails,
  StyleDefiningAttribute,
} from '@shared/data-access/interfaces';
import { AnalyticsService, StoreService, UtilityService } from '@shared/data-access/services';
import { Injectable, inject } from '@angular/core';

import { AttributeType } from './attribute-type.enum';
import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { ProductPageTemplateType } from './page-template-type.enum';
import { ProductService } from '@shared/data-access/product';

// Local type for product analytics data (combines ProductsData and Bundle types)
export type ProductDataForAnalytics = (
  | ProductsData
  | (BundleData & { ecodes: ProductsData[]; pageTemplate: string })
) & {
  pageTemplate?: string;
  skus?: ProductSku[];
  style?: ProductStyle;
};

type ProductAnalyticsQueryParams = {
  color?: unknown;
  sku?: unknown;
};

/** @dynamic */
@Injectable({
  providedIn: 'root',
})
export class ProductAnalyticsService extends AnalyticsService {
  attentiveImageUrl?: string;

  protected readonly storeService = inject(StoreService);
  protected readonly utilityService = inject(UtilityService);
  private readonly productService = inject(ProductService);

  /**
   * @description
   * Posts to rumrunner when a missing product image is detected
   */
  public emitMissingImageEvent(productInfo: MissingImageEventInfo): void {
    const elkRUM = (
      globalThis as { elkRUM?: { setCustom?: (key: string, value: unknown) => void } }
    )['elkRUM'];
    if (elkRUM?.setCustom) {
      elkRUM.setCustom('missing_image', productInfo);
    }
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setProductDetails
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param productDetails - Array of product details
   *
   * @description
   * Sets the SKU details of product
   */
  public setProductDetails(productDetails: object[]): void {
    this.siteData.ProductDetails = productDetails;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setProductArray
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param productArray - The array of products to set
   *
   * @description
   * Sets an array of products on the Object.
   */
  public setProductArray(productArray: object): void {
    this.siteData.ProductArray = productArray;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setProductId
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param productId - The product part number
   *
   * @description
   * Sets the Product ID on the Object. ProductID ID is the eCode that is represented
   * on the PDP. It can be found on the PDP before you resolve to a SKU
   */
  public setProductId(productId: string): void {
    this.siteData.ProductID = productId;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setProductName
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param productName - The product name
   *
   * @description
   * Sets the Product Name on the Object. Product Name represents the short description associated to the product.
   */
  public setProductName(productName: string): void {
    this.siteData.ProductName = productName;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setProductShippingRestriction
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param shippingRestrictionFlag - Flag if there is a shipping restriction on the product
   *
   * @description
   * Sets a flag on the Object whether there is a shipping restriction or not.
   */
  public setProductShippingRestriction(shippingRestrictionFlag: string): void {
    this.siteData.ProductShippingRestriction = shippingRestrictionFlag;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setNikeConnectedProduct
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param shippingRestrictionFlag - Flag if the product is nike exclusive
   *
   * @description
   * Sets a flag on the Object whether there is a nike exclusive product or not.
   */
  public setNikeConnectedProduct(nikeConnectedProductFlag: boolean): void {
    this.siteData.nikeConnectedProduct = nikeConnectedProductFlag;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setTechSpecPresent
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param techSpecFlag - Whether technical specifications are present or not
   *
   * @description
   * Sets technical specifications flag on the Object as to whether they are present on the product or not.
   */
  public setTechSpecPresent(techSpecFlag: string): void {
    this.siteData.TechSpecPresent = techSpecFlag;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setProductPageDetailsProperties
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @description
   * Sets the product details page properties for the resolved product.
   */

  setProductPageDetailsProperties(
    product: ProductDataForAnalytics,
    skuLevelPricingBuckets: AnalyticsPricingBuckets,
    inventoryMapEcomm: ISkuLocation[],
    inventoryMapBOPIS: ISkuLocation[],
    loyaltyBalance: LoyaltyBalance,
    shippingInfo: { isHazmat?: boolean; isRestricted?: boolean },
    queryParams: ProductAnalyticsQueryParams = {},
  ) {
    // Array of product details
    const productDetails: Array<Record<string, unknown>> = [];

    const bundledProducts =
      product.pageTemplate === ProductPageTemplateType.BUNDLE && 'ecodes' in product
        ? product.ecodes
        : undefined;

    // Set the page template
    if (!this.siteData.PageTemplate) {
      this.setPageTemplate(product.pageTemplate || 'Standard');
    }

    // Set the product ID
    const productAsBundleData = product as Partial<BundleData>;
    const productId = productAsBundleData.partNumber ?? product.style?.partNumber ?? '';
    this.setProductId(productId);

    // Set the product name
    const productName = productAsBundleData.name ?? product.style?.name ?? '';
    this.setProductName(productName);

    if (bundledProducts) {
      for (let i = 0, length = bundledProducts.length; i < length; i += 1) {
        productDetails.push(
          this.getProductDetailsWithSelectedColor(
            bundledProducts[i],
            skuLevelPricingBuckets,
            inventoryMapEcomm,
            inventoryMapBOPIS,
          ),
        );
      }
    } else {
      productDetails.push(
        this.getProductDetailsWithSelectedColor(
          product,
          skuLevelPricingBuckets,
          inventoryMapEcomm,
          inventoryMapBOPIS,
          this.getQueryParamValue(queryParams.color),
          this.getQueryParamValue(queryParams.sku),
        ),
      );
    }

    // Set product details
    this.setProductDetails(productDetails);

    // Set shipping restrictions
    this.setProductShippingRestriction(
      shippingInfo.isHazmat || shippingInfo.isRestricted
        ? DCSG_CONSTANTS.analyticsReporting.boolean.true
        : DCSG_CONSTANTS.analyticsReporting.boolean.false,
    );

    // Set technical specifications
    this.setTechSpecPresent(
      product.style?.techSpecs
        ? DCSG_CONSTANTS.analyticsReporting.boolean.true
        : DCSG_CONSTANTS.analyticsReporting.boolean.false,
    );

    if (loyaltyBalance && 'rewardAmount' in loyaltyBalance) {
      // set rewardValue
      this.setRewardValue(loyaltyBalance.rewardAmount);
    }
  }

  public sendAttentiveHeroImage(imageUrl: string, productId: string) {
    if (imageUrl !== this.attentiveImageUrl) {
      this.triggerAnalyticsEvent('attentiveImageUrl', {
        ProductID: productId,
        ProductImageUrl: imageUrl,
      });
      this.attentiveImageUrl = imageUrl;
    }
  }

  private getProductDetailsWithSelectedColor(
    product: ProductDataForAnalytics,
    skuLevelPricingBuckets: AnalyticsPricingBuckets,
    inventoryMapEcomm: ISkuLocation[],
    inventoryMapBOPIS: ISkuLocation[],
    colorQueryParam?: string,
    skuIdQueryParam?: string,
  ) {
    const productDetails = this.getProductDetailsQV(
      product,
      skuLevelPricingBuckets,
      inventoryMapEcomm,
      inventoryMapBOPIS,
    );
    const selectedColor = this.productService.getInitialSelectedColor(
      product.style?.definingAttributes,
      product.skus,
      colorQueryParam,
      skuIdQueryParam,
    );

    return selectedColor ? { ...productDetails, SelectedColor: selectedColor } : productDetails;
  }

  private getProductDetailsQV(
    product: ProductDataForAnalytics,
    skuLevelPricingBuckets: AnalyticsPricingBuckets,
    inventoryMapEcomm: ISkuLocation[],
    inventoryMapBOPIS: ISkuLocation[],
  ) {
    // Defining attributes of the product
    const productAttributeList: Array<{ Type: string; Name: string; CountOfOptions: number }> = [];

    // SKU details
    const skuDetailsList: SKUDetails[] = [];

    // See Price in Cart String
    const EMPTY_PRICE_STRING = 'See Price in Cart';

    let productGender: string | undefined;

    let productType: string | undefined;

    // returning true to siteData if product is nike ape exclusive
    const nikeConnectedProduct =
      product.skus?.some(
        (obj: ProductSku) =>
          (obj as any).descAttributesParsed?.ApeExclusive === 'Nike APE Exclusive',
      ) ?? false;

    this.setNikeConnectedProduct(nikeConnectedProduct);

    // Getting Gender and product Type value and returning it to productDetails
    if (
      product.style?.descriptiveAttributes &&
      Array.isArray(product.style.descriptiveAttributes)
    ) {
      product.style.descriptiveAttributes.forEach(
        (obj: { name?: string; value?: string | number }) => {
          if (obj?.name === 'Gender') {
            productGender = obj.value?.toString() ?? '';
          } else if (obj?.name === 'Product Type') {
            productType = obj.value?.toString() ?? '';
          }
        },
      );
    }

    // Iterate through defining attributes if available
    if (product.style?.definingAttributes) {
      product.style.definingAttributes.forEach((attribute: StyleDefiningAttribute) => {
        // Default the attribute value count to 0
        let attrValueCount = 0;

        // Attempt to split the attribute values
        if (attribute.value.length > 0) {
          attrValueCount = attribute.value.split('|').length;
        }

        // Default attribute type to `AttributeType.BLOCKS`
        let attrType = AttributeType.BLOCKS;

        // Check to see if attribute is `color`
        if (typeof attribute.name === 'string' && attribute.name.toLowerCase() === 'color') {
          // Update the attribute type to `AttributeType.SWATCH`
          attrType = AttributeType.SWATCH;
        }

        // Add attribute to Array
        productAttributeList.push({
          Type: attrType,
          Name: attribute.name,
          CountOfOptions: attrValueCount,
        });
      });
    }

    // Iterate through SKUs if available
    if (product.skus) {
      // Sets to true by default
      let isAddtoCartAvailable = DCSG_CONSTANTS.analyticsReporting.boolean.true;

      // Sets to false if product is in store only
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- invFlag, bopisFlag not in shared scope interface
      const styleWithFlags = product.style as any;
      if (styleWithFlags?.isaFlag && !styleWithFlags?.invFlag && !styleWithFlags?.bopisFlag) {
        isAddtoCartAvailable = DCSG_CONSTANTS.analyticsReporting.boolean.false;
      }

      product.skus?.forEach((sku: ProductSku) => {
        // eComm inventory
        const skuInventoryEcomm = this.storeService.getSkuInventoryFromLocations(
          inventoryMapEcomm,
          sku.partNumber,
        );

        // BOPIS inventory
        const skuInventoryBOPIS = this.storeService.getSkuInventoryFromLocations(
          inventoryMapBOPIS,
          sku.partNumber,
        );

        // Add the SKU details to the list
        skuDetailsList.push({
          ProductSKU: sku.partNumber,
          SKUCurrentPrice: `${
            sku.prices.offerPrice < sku.prices.mapPrice
              ? EMPTY_PRICE_STRING
              : sku.prices.offerPrice || EMPTY_PRICE_STRING
          }`,
          SKUListPrice: `${sku.prices.listPrice || EMPTY_PRICE_STRING}`,
          BOPISAvailable: `${sku.bopisFlag}`,
          AddtoCartAvailable: isAddtoCartAvailable,
          ISAAvailable: `${sku.isaFlag}`,
          ShipClass: `${sku.shipClass}`,
          AvailableForPickup: skuInventoryBOPIS
            ? `${this.utilityService.isTruthy((skuInventoryBOPIS.inventory ?? 0) > 0) && sku.bopisFlag}`
            : DCSG_CONSTANTS.analyticsReporting.boolean.false,
          AvailableInStore: skuInventoryBOPIS
            ? `${this.utilityService.isTruthy((skuInventoryBOPIS.isaInventory ?? 0) > 0) && sku.isaFlag}`
            : DCSG_CONSTANTS.analyticsReporting.boolean.false,
          AvailabletoShip: skuInventoryEcomm
            ? `${this.utilityService.isTruthy((skuInventoryEcomm.inventory ?? 0) > 0)}`
            : DCSG_CONSTANTS.analyticsReporting.boolean.false,
          WebActiveDate: this.formatWebActiveDate(sku.webActiveDate),
          WarrantyEligible: sku.warrantyEligible ?? false,
          attributes:
            sku.definingAttributes?.map((attribute) => ({
              Type: attribute?.name ?? '',
              Value: attribute?.value ?? '',
            })) ?? [],
        });
      });
    }

    const manufacturer = product.style?.descriptiveAttributes?.find(
      (descAttr: { identifier?: string; value?: string | number }) =>
        descAttr.identifier === 'X_BRAND',
    );

    return {
      ProductAttributeList: productAttributeList,
      ProductBrand: manufacturer?.value || '',
      ProductName: product.style?.name || '',
      ProductID: product.style?.partNumber || '',
      ProductDescriptiveAttributes: product.style?.descriptiveAttributes || [],
      ProductGender: productGender ?? '',
      ProductType: productType ?? '',
      ProductSKUDetails: skuDetailsList,
      SKUPriceingOptions: skuLevelPricingBuckets,
    };
  }

  private formatWebActiveDate(webActiveDate: string): string {
    let formattedDate = 'undefined';
    if (webActiveDate?.includes('T')) {
      // Validate that the part before the "T" matches "YYYY-MM-DD"
      const datePart = webActiveDate.split('T')[0];
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

      if (dateRegex.test(datePart)) {
        const dateString = datePart.split('-');
        formattedDate = `${dateString[0]}/${dateString[1]}/${dateString[2]}`;
      }
    }
    return formattedDate;
  }

  private getQueryParamValue(value: unknown): string | undefined {
    if (typeof value === 'string') {
      return value;
    }

    if (Array.isArray(value) && typeof value[0] === 'string') {
      return value[0];
    }

    return undefined;
  }
}
